import { dependabilityV2Colors } from '@/styles/colors';
import { tierColor } from './tierColors';
import { clampScore, displayScore } from './score';

interface Props {
  score: number;
  tier: string;
  size?: number;
}

const ScoreRing = ({ score, tier, size = 100 }: Props) => {
  // Rendered Figma export: 100-box with outer radius 50 and inner 40.6.
  const strokeWidth = size * 0.094;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = clampScore(score);
  const center = size / 2;
  const shownScore = displayScore(score);
  // GRAVY-10233: the ring (arc + digits) uses the flat TIER color — the same
  // `tierColor(tier)` the tier pill and the popover's highlighted segment use —
  // so a single tier color appears everywhere on the card. It previously carried
  // its own per-tier accents (a green gradient arc + green digits for Gold while
  // the pill was olive), putting two "tier colors" on one screen. Per the ticket
  // that was an inconsistency to fix, not a Figma pattern to replicate.
  const color = tierColor(tier);

  return (
    <svg
      width={size}
      height={size}
      role="img"
      aria-label={`Dependability score ${shownScore} out of 100`}
    >
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke={dependabilityV2Colors.depMeterTrack}
        strokeWidth={strokeWidth}
      />
      <circle
        data-testid="score-ring-progress"
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - clamped / 100)}
        transform={`rotate(-90 ${center} ${center})`}
      />
      <text
        x={center}
        y={center}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={size * 0.267}
        fontWeight={600}
        fill={color}
      >
        {shownScore}
      </text>
    </svg>
  );
};

export default ScoreRing;
